import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { OwnerEmailPreview } from '@/api/ma-tax-schemas'
import { useToast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { ErrorState } from '@/components/ui/error-state'

function SentBadge({ sent }: { sent: OwnerEmailPreview['sent'] }) {
  if (!sent) return <Badge variant="secondary">Not sent</Badge>
  if (sent.status === 'sent') return <Badge variant="success">Sent</Badge>
  if (sent.status === 'sending') return <Badge variant="warning">Sending</Badge>
  return (
    <Badge variant="destructive" title={sent.error ?? undefined}>
      Failed
    </Badge>
  )
}

/**
 * Owner filing emails for one submitted version (CAP-550). Shows exactly what
 * each owner would receive and to whom; nothing is sent until the approval
 * button, and each owner can only be emailed once per filing.
 */
export function OwnerEmailsPanel({ versionId }: { versionId: string }) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState<string | null>(null)
  const [approving, setApproving] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const query = useQuery({
    queryKey: ['roc-owner-emails', versionId],
    queryFn: () => api.getOwnerEmailPreviews(versionId),
  })

  if (query.isLoading) return <LoadingSkeleton className="h-16 w-full" />
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />
  const data = query.data
  if (!data) return null
  if (!data.ready) return <p className="text-sm text-textMuted">{data.reason}</p>

  const sendable = data.previews.filter(
    p => p.recipients.length > 0 && (!p.sent || p.sent.status === 'failed')
  )

  const approve = async () => {
    setApproving(true)
    try {
      const result = await api.sendOwnerEmails(
        versionId,
        sendable.map(p => p.property_id)
      )
      const sent = result.outcomes.filter(o => o.status === 'sent').length
      const failed = result.outcomes.filter(o => o.status === 'failed').length
      toast({
        title: `Sent ${sent} owner ${sent === 1 ? 'email' : 'emails'}`,
        description: failed ? `${failed} failed; they can be retried.` : undefined,
        variant: failed ? 'destructive' : undefined,
      })
    } catch (err: unknown) {
      toast({
        title: 'Owner emails were not sent',
        description: err instanceof Error ? err.message : undefined,
        variant: 'destructive',
      })
    } finally {
      setApproving(false)
      setConfirming(false)
      await queryClient.invalidateQueries({ queryKey: ['roc-owner-emails', versionId] })
    }
  }

  return (
    <div className="space-y-4">
      <div className="text-sm text-textMuted">
        Confirmation{' '}
        <span className="font-mono text-text">{data.confirmation?.confirmation_number}</span> ·
        account <span className="font-mono text-text">{data.confirmation?.account_id}</span> ·
        submitted {data.confirmation?.submitted_at_raw}. Every email is BCC&apos;d to {data.bcc}.
      </div>
      <ul className="divide-y divide-border rounded-card border border-border">
        {data.previews.map(p => (
          <li key={p.property_id} className="space-y-2 px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-sm font-medium text-text">{p.property_name}</div>
                <div className="text-xs text-textMuted">
                  {p.recipients.length
                    ? `To: ${p.recipients.map(r => r.email).join(', ')}`
                    : p.problems.join('; ')}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <SentBadge sent={p.sent} />
                <Button
                  size="sm"
                  variant="ghost"
                  aria-expanded={open === p.property_id}
                  onClick={() => setOpen(open === p.property_id ? null : p.property_id)}
                >
                  {open === p.property_id ? 'Hide email' : 'Preview email'}
                </Button>
              </div>
            </div>
            {open === p.property_id && (
              <div className="space-y-1">
                <div className="text-xs text-textMuted">Subject: {p.subject}</div>
                <iframe
                  title={`Email preview for ${p.property_name}`}
                  sandbox=""
                  srcDoc={p.html}
                  className="h-[640px] w-full rounded-card border border-border bg-white"
                />
              </div>
            )}
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center justify-end gap-3">
        {sendable.length === 0 ? (
          <span className="text-sm text-textMuted">Nothing left to send for this filing.</span>
        ) : confirming ? (
          <>
            <span className="text-sm text-amber">
              Email {sendable.length} {sendable.length === 1 ? 'owner' : 'owners'} now?
            </span>
            <Button variant="ghost" onClick={() => setConfirming(false)} disabled={approving}>
              Cancel
            </Button>
            <Button onClick={approve} disabled={approving}>
              {approving ? 'Sending...' : 'Yes, send'}
            </Button>
          </>
        ) : (
          <Button onClick={() => setConfirming(true)}>
            Approve and email {sendable.length} {sendable.length === 1 ? 'owner' : 'owners'}
          </Button>
        )}
      </div>
    </div>
  )
}
